/**
 * Melodiza Playlist Engine v2
 * Multi-factor intelligent track sequencing:
 * - transitionScore: Camelot 0.35 / BPM 0.25 / energy 0.20 / history 0.15 / context 0.05
 * - MMR rerank (λ=0.7) for diversity
 * - Hard-veto 5 recent tracks
 * - Epsilon-greedy 10% exploration
 */

// Camelot wheel compatibility - numbers that mix well
const CAMELOT_COMPATIBLE = {
  "1A": ["1A", "1B", "12A", "2A"],
  "2A": ["2A", "2B", "1A", "3A"],
  "3A": ["3A", "3B", "2A", "4A"],
  "4A": ["4A", "4B", "3A", "5A"],
  "5A": ["5A", "5B", "4A", "6A"],
  "6A": ["6A", "6B", "5A", "7A"],
  "7A": ["7A", "7B", "6A", "8A"],
  "8A": ["8A", "8B", "7A", "9A"],
  "9A": ["9A", "9B", "8A", "10A"],
  "10A": ["10A", "10B", "9A", "11A"],
  "11A": ["11A", "11B", "10A", "12A"],
  "12A": ["12A", "12B", "11A", "1A"],
  "1B": ["1B", "1A", "12B", "2B"],
  "2B": ["2B", "2A", "1B", "3B"],
  "3B": ["3B", "3A", "2B", "4B"],
  "4B": ["4B", "4A", "3B", "5B"],
  "5B": ["5B", "5A", "4B", "6B"],
  "6B": ["6B", "6A", "5B", "7B"],
  "7B": ["7B", "7A", "6B", "8B"],
  "8B": ["8B", "8A", "7B", "9B"],
  "9B": ["9B", "9A", "8B", "10B"],
  "10B": ["10B", "10A", "9B", "11B"],
  "11B": ["11B", "11A", "10B", "12B"],
  "12B": ["12B", "12A", "11B", "1B"],
};

// Energy profiles by time of day (kept for generatePlaylist backward-compat)
const TIME_PROFILES = {
  morning: { targetEnergy: 30, maxBpm: 100, mood: "calm" },
  lunch: { targetEnergy: 50, maxBpm: 115, mood: "upbeat" },
  afternoon: { targetEnergy: 45, maxBpm: 110, mood: "moderate" },
  evening: { targetEnergy: 35, maxBpm: 100, mood: "lounge" },
  night: { targetEnergy: 60, maxBpm: 130, mood: "energetic" },
};

// Business presets (kept for generatePlaylist backward-compat)
const BUSINESS_PROFILES = {
  restaurant: { maxEnergy: 50, maxBpm: 110, preferInstrumental: true, genres: ["jazz", "lounge", "bossa", "chill"] },
  bar:        { maxEnergy: 75, maxBpm: 128, preferInstrumental: false, genres: ["deep-house", "urban", "lounge", "electro"] },
  salon:      { maxEnergy: 40, maxBpm: 100, preferInstrumental: true, genres: ["chill", "lounge", "zen", "jazz"] },
  boutique:   { maxEnergy: 60, maxBpm: 120, preferInstrumental: false, genres: ["pop", "chill", "lounge", "urban"] },
  spa:        { maxEnergy: 25, maxBpm: 85,  preferInstrumental: true, genres: ["zen", "chill"] },
  gym:        { maxEnergy: 90, maxBpm: 140, preferInstrumental: false, genres: ["electro", "pop", "urban", "deep-house"] },
  hotel:      { maxEnergy: 40, maxBpm: 105, preferInstrumental: true, genres: ["lounge", "jazz-swing", "chill", "bossa-nova"] },
  office:     { maxEnergy: 60, maxBpm: 110, preferInstrumental: true, genres: ["lounge", "ambient", "chill", "jazz-swing"] },
};

// ─── Energy curves (Step 5) ──────────────────────────────────────────────────

// Energy curves: target energy per (businessType, timeOfDay) — 0-100 scale
const ENERGY_CURVES = {
  restaurant: { morning: 30, lunch: 50, afternoon: 40, evening: 45, night: 50 },
  bar:        { morning: 40, lunch: 55, afternoon: 60, evening: 75, night: 80 },
  salon:      { morning: 30, lunch: 35, afternoon: 35, evening: 30, night: 30 },
  boutique:   { morning: 45, lunch: 60, afternoon: 55, evening: 50, night: 50 },
  spa:        { morning: 20, lunch: 25, afternoon: 25, evening: 25, night: 25 },
  gym:        { morning: 75, lunch: 80, afternoon: 75, evening: 85, night: 85 },
  hotel:      { morning: 35, lunch: 45, afternoon: 40, evening: 40, night: 35 },
  office:     { morning: 50, lunch: 55, afternoon: 50, evening: 45, night: 40 },
};

const ENERGY_CURVE_DEFAULT = 50;

/**
 * getContextScore — score 0-100 how well candidateEnergy matches target energy curve.
 * Score 100 = exact match; score 0 = delta ≥ 50.
 */
function getContextScore(businessType, timeOfDay, candidateEnergy) {
  const curve = ENERGY_CURVES[businessType];
  if (!curve) return ENERGY_CURVE_DEFAULT;
  const target = curve[timeOfDay] || ENERGY_CURVE_DEFAULT;
  const delta = Math.abs((candidateEnergy != null ? candidateEnergy : 50) - target);
  return Math.max(0, 100 - delta * 2);
}

// ─── History decay (Step 4 / Step 6) ────────────────────────────────────────

const HISTORY_HALF_LIFE_MS = 2 * 60 * 60 * 1000; // 2h

/**
 * historyDecayBoost — returns [0, 100] malus for anti-repetition.
 * Value = 50 at t=0 (just played), 25 at t=2h, 12.5 at t=4h.
 * Caller uses (100 - historyDecayBoost) as history component in score.
 */
function historyDecayBoost(trackId, history, halfLifeMs = HISTORY_HALF_LIFE_MS) {
  if (!Array.isArray(history) || history.length === 0) return 0;
  const now = Date.now();
  let totalBoost = 0;
  for (const h of history) {
    if (h.trackId !== trackId) continue;
    const age = now - (h.playedAt || 0);
    // Exponential decay: weight = 0.5^(age / halfLife)
    const weight = Math.pow(0.5, age / halfLifeMs);
    totalBoost += weight * 50; // 50 points per recent play, halved every 2h
  }
  return Math.min(100, totalBoost);
}

// ─── v2 transitionScore ──────────────────────────────────────────────────────

/**
 * transitionScore v2 — multi-factor weighted score (0-100).
 * Weights: Camelot 0.35 / BPM 0.25 / energy 0.20 / history 0.15 / context 0.05
 * Sum = 1.00
 */
function transitionScore(trackA, trackB, ctx = {}) {
  if (!trackA || !trackB) return 0;
  const { historyBoost = 100, contextScore = ENERGY_CURVE_DEFAULT } = ctx;

  // 1. BPM similarity (±10 BPM = perfect)
  const bpmDiff = Math.abs((trackA.bpm || 100) - (trackB.bpm || 100));
  const bpmScore = Math.max(0, 100 - bpmDiff * 3);

  // 2. Energy similarity (±15 = perfect)
  const energyDiff = Math.abs((trackA.energy || 50) - (trackB.energy || 50));
  const energyScore = Math.max(0, 100 - energyDiff * 2.5);

  // 3. Camelot key compatibility
  const compatible = CAMELOT_COMPATIBLE[trackA.camelot] || [];
  const keyScore = compatible.includes(trackB.camelot) ? 100 : 30;

  // 4. History component: historyBoost = (100 - historyMalus) passed by caller
  // historyBoost ∈ [0, 100] — high means track hasn't been played recently

  // 5. Context score (energy curve match)
  // contextScore ∈ [0, 100]

  // Weighted (Phase 1 weights; sum = 1.00)
  return Math.round(
    keyScore       * 0.35 +
    bpmScore       * 0.25 +
    energyScore    * 0.20 +
    historyBoost   * 0.15 +
    contextScore   * 0.05
  );
}

// ─── trackSimilarity (for MMR) ───────────────────────────────────────────────

function trackSimilarity(a, b) {
  const bpmSim    = Math.max(0, 100 - Math.abs((a.bpm || 100) - (b.bpm || 100)) * 3);
  const energySim = Math.max(0, 100 - Math.abs((a.energy || 50) - (b.energy || 50)) * 2.5);
  const sameSubgenre = a.subgenre && b.subgenre && a.subgenre === b.subgenre ? 100 : 0;
  return (bpmSim + energySim + sameSubgenre) / 3;
}

// ─── MMR rerank ──────────────────────────────────────────────────────────────

/**
 * mmrRerank — Maximal Marginal Relevance rerank for diversity.
 * λ=0.7 (70% relevance, 30% diversity).
 */
function mmrRerank(scored, lambda = 0.7, k = 10) {
  if (scored.length <= 1) return scored;
  const selected = [scored[0]];
  const remaining = scored.slice(1);
  while (selected.length < k && remaining.length > 0) {
    let bestIdx = 0;
    let bestMmr = -Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const cand = remaining[i];
      let maxSim = 0;
      for (const sel of selected) {
        const sim = trackSimilarity(cand.track, sel.track);
        if (sim > maxSim) maxSim = sim;
      }
      const mmr = lambda * cand.score - (1 - lambda) * maxSim;
      if (mmr > bestMmr) {
        bestMmr = mmr;
        bestIdx = i;
      }
    }
    selected.push(remaining[bestIdx]);
    remaining.splice(bestIdx, 1);
  }
  return selected;
}

// ─── Epsilon-greedy ──────────────────────────────────────────────────────────

const EPSILON = 0.1;

function pickWithExploration(rerankedTop) {
  if (Math.random() < EPSILON && rerankedTop.length > 1) {
    // Exploration: random within top-N
    return rerankedTop[Math.floor(Math.random() * rerankedTop.length)].track;
  }
  // Exploitation: best score
  return rerankedTop[0].track;
}

// ─── getSmartNextTrack v2 ─────────────────────────────────────────────────────

/**
 * getSmartNextTrack v2 — multi-factor scoring with MMR + epsilon-greedy.
 *
 * @param {object} currentTrack - track currently playing
 * @param {Array}  history      - [{trackId, playedAt}]
 * @param {Array}  allTracks    - full catalogue
 * @param {object} opts         - { excludeIds, contextScore, businessType, timeOfDay, enableExploration, enableMMR }
 * @returns {object|null}       - next track or null
 */
function getSmartNextTrack(currentTrack, history, allTracks, opts = {}) {
  const {
    excludeIds = [],
    businessType = null,
    timeOfDay = null,
    enableExploration = true,
    enableMMR = true,
  } = opts;

  if (!allTracks || allTracks.length === 0) return null;

  if (!currentTrack) {
    // No current track → pick random from catalogue
    const pool = allTracks.filter((t) => !excludeIds.includes(t.id));
    if (pool.length === 0) return null;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  // 1. Hard-veto: 5 most recent + currentTrack + excludeIds
  const recent5 = (history || []).slice(0, 5).map((h) => h.trackId);
  const exclude = new Set([...excludeIds, currentTrack.id, ...recent5].filter(Boolean));

  // 2. Pool: same subgenre first, fallback genre, fallback total
  let pool = allTracks.filter((t) => !exclude.has(t.id));
  if (currentTrack.subgenre) {
    const subPool = pool.filter((t) => t.subgenre === currentTrack.subgenre);
    if (subPool.length >= 5) {
      pool = subPool;
    } else if (currentTrack.genre) {
      const genrePool = pool.filter((t) => t.genre === currentTrack.genre);
      if (genrePool.length >= 5) pool = genrePool;
    }
  }

  if (pool.length === 0) return null;

  // 3. Score each candidate
  const scored = pool.map((t) => {
    const historyMalus = historyDecayBoost(t.id, history);
    const ctxScore = (businessType && timeOfDay)
      ? getContextScore(businessType, timeOfDay, t.energy)
      : ENERGY_CURVE_DEFAULT;
    const ctx = { historyBoost: 100 - historyMalus, contextScore: ctxScore };
    return {
      track: t,
      score: transitionScore(currentTrack, t, ctx),
    };
  });
  scored.sort((a, b) => b.score - a.score);

  // 4. MMR rerank top-20 → top-10 (diversity)
  const reranked = enableMMR
    ? mmrRerank(scored.slice(0, 20), 0.7, 10)
    : scored.slice(0, 10);

  // 5. Epsilon-greedy pick
  return enableExploration ? pickWithExploration(reranked) : reranked[0].track;
}

/**
 * getSmartNextTrackV1 — preserved v1 for rollback / A/B comparison.
 * Uses popularity + play-count scoring (original algo).
 */
function getSmartNextTrackV1(currentTrack, history, allTracks, opts = {}) {
  const { shuffle = true, excludeIds = [] } = opts;
  if (!allTracks || allTracks.length === 0) return null;
  if (!currentTrack) {
    const pool = allTracks.filter((t) => !excludeIds.includes(t.id));
    if (pool.length === 0) return null;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  const exclude = new Set([...excludeIds, currentTrack.id]);
  let pool = allTracks.filter(
    (t) =>
      !exclude.has(t.id) &&
      t.subgenre &&
      currentTrack.subgenre &&
      t.subgenre.toLowerCase() === currentTrack.subgenre.toLowerCase()
  );
  if (pool.length === 0) {
    pool = allTracks.filter(
      (t) =>
        !exclude.has(t.id) &&
        t.genre &&
        currentTrack.genre &&
        t.genre.toLowerCase() === currentTrack.genre.toLowerCase()
    );
  }
  if (pool.length === 0) pool = allTracks.filter((t) => !exclude.has(t.id));
  if (pool.length === 0) return null;

  const counts = new Map();
  if (Array.isArray(history)) {
    for (const h of history) counts.set(h.trackId, (counts.get(h.trackId) || 0) + 1);
  }

  const scored = pool.map((t) => ({
    track: t,
    score: (typeof t.popularity === 'number' ? t.popularity : 50) + (counts.get(t.id) || 0) * 10,
  }));
  scored.sort((a, b) => b.score - a.score);

  if (shuffle) {
    const top = scored.slice(0, Math.min(5, scored.length));
    const totalWeight = top.reduce((s, x) => s + Math.max(1, x.score), 0);
    let r = Math.random() * totalWeight;
    for (const x of top) {
      r -= Math.max(1, x.score);
      if (r <= 0) return x.track;
    }
    return top[0].track;
  }

  return scored[0].track;
}

// ─── generatePlaylist (unchanged, backward-compat) ───────────────────────────

/**
 * Generate an intelligent playlist using greedy nearest-neighbor.
 */
function generatePlaylist(allTracks, options = {}) {
  const {
    genres = [],
    businessType = null,
    timeOfDay = null,
    maxTracks = 50,
    avoidIds = [],
  } = options;

  let pool = [...allTracks];

  if (genres.length > 0) pool = pool.filter((t) => genres.includes(t.genre));

  if (businessType && BUSINESS_PROFILES[businessType]) {
    const profile = BUSINESS_PROFILES[businessType];
    pool = pool.filter(
      (t) =>
        t.energy <= profile.maxEnergy &&
        t.bpm <= profile.maxBpm &&
        (genres.length > 0 || profile.genres.includes(t.genre))
    );
  }

  if (timeOfDay && TIME_PROFILES[timeOfDay]) {
    const tp = TIME_PROFILES[timeOfDay];
    pool = pool.filter((t) => t.bpm <= tp.maxBpm);
  }

  pool = pool.filter((t) => !avoidIds.includes(t.id));

  if (pool.length === 0) return [];

  const playlist = [];
  let targetEnergy = timeOfDay ? TIME_PROFILES[timeOfDay].targetEnergy : 40;
  let current = pool.reduce((best, t) =>
    Math.abs(t.energy - targetEnergy) < Math.abs(best.energy - targetEnergy) ? t : best
  );

  playlist.push(current);
  pool = pool.filter((t) => t.id !== current.id);

  while (playlist.length < maxTracks && pool.length > 0) {
    let bestTrack = null;
    let bestScore = -1;
    for (const candidate of pool) {
      const score = transitionScore(current, candidate);
      if (score > bestScore) {
        bestScore = score;
        bestTrack = candidate;
      }
    }
    if (bestTrack) {
      playlist.push(bestTrack);
      pool = pool.filter((t) => t.id !== bestTrack.id);
      current = bestTrack;
    } else {
      break;
    }
  }

  return playlist;
}

/**
 * Get current time of day profile key.
 */
function getCurrentTimeProfile() {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 11) return "morning";
  if (hour >= 11 && hour < 14) return "lunch";
  if (hour >= 14 && hour < 18) return "afternoon";
  if (hour >= 18 && hour < 21) return "evening";
  return "night";
}

// ─── Phase 3 Step 7 — Smart Shuffle 2-opt + Mood Arc ─────────────────────────

/**
 * fisherYates — standard random shuffle (reference impl for 2-opt baseline).
 */
function fisherYates(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * totalBadness — sum of (1 - transitionScore) over consecutive pairs.
 * Lower = better (transitions are more harmonious).
 */
function totalBadness(seq) {
  let s = 0;
  for (let k = 0; k < seq.length - 1; k++) {
    s += 1 - transitionScore(seq[k], seq[k + 1]) / 100; // transitionScore returns 0-100
  }
  return s;
}

/**
 * smartShuffle2Opt — 2-opt local search optimizer.
 *
 * Minimizes total transitionScore "badness" across consecutive pairs via
 * iterated 2-opt swaps (reverse a sub-segment if it improves the score).
 *
 * @param {Array}  playlist         - array of track objects
 * @param {number} shuffleStrength  - 0.0 = pure Fisher-Yates; 1.0 = full 2-opt optimization
 * @param {number} iterations       - max improvement iterations (default 50)
 * @returns {Array} - reordered playlist
 *
 * Quality gate: on 50-track playlist, totalBadness(2opt) <= totalBadness(fisher_yates) * 0.90
 * (≥10% reduction). Feature flag: PostHog 'smart_shuffle' variant 'two_opt' (A/B test post-ship).
 */
export function smartShuffle2Opt(playlist, shuffleStrength = 1.0, iterations = 50) {
  if (!playlist || playlist.length < 3 || shuffleStrength === 0) {
    return fisherYates(playlist || []);
  }

  let best = fisherYates(playlist);
  let bestScore = totalBadness(best);

  for (let iter = 0; iter < iterations; iter++) {
    // Pick random segment [i..j] (i < j)
    const i = Math.floor(Math.random() * (best.length - 1));
    const j = i + 1 + Math.floor(Math.random() * (best.length - i - 1));

    // shuffleStrength controls acceptance rate: at 1.0 all swaps evaluated,
    // at 0.5 only 50% of swaps are tried (adds randomness vs optimization)
    if (Math.random() > shuffleStrength) continue;

    // 2-opt: reverse segment [i..j] in a copy
    const candidate = [...best.slice(0, i), ...best.slice(i, j + 1).reverse(), ...best.slice(j + 1)];
    const score = totalBadness(candidate);
    if (score < bestScore) {
      best = candidate;
      bestScore = score;
    }
  }

  return best;
}

/**
 * mapHourToTimeOfDay — helper for moodArc time-based energy targeting.
 */
function mapHourToTimeOfDay(h) {
  if (h >= 5 && h < 11) return 'morning';
  if (h >= 11 && h < 14) return 'lunch';
  if (h >= 14 && h < 18) return 'afternoon';
  if (h >= 18 && h < 22) return 'evening';
  return 'night';
}

/**
 * moodArc — generate a playlist following a business energy curve.
 *
 * Energy target varies by time-of-day per ENERGY_CURVES[businessType].
 * Tracks are selected to match the energy target at each point in time.
 * Transition quality is maximized within each energy band.
 *
 * @param {Array}  allTracks     - full track catalogue
 * @param {string} businessType  - key in ENERGY_CURVES (e.g. 'cafe', 'restaurant', 'bar')
 * @param {number} durationHours - playlist duration in hours (default 4)
 * @returns {Array} - ordered playlist following mood arc
 */
export function moodArc(allTracks, businessType = 'cafe', durationHours = 4) {
  if (!allTracks || allTracks.length === 0) return [];
  const curve = ENERGY_CURVES[businessType];
  const tracksPerHour = 17; // ~3.5 min/track average
  const totalTracks = tracksPerHour * durationHours;
  const startHour = new Date().getHours();
  const usedIds = new Set();
  const result = [];

  for (let i = 0; i < totalTracks; i++) {
    const tProgress = i / totalTracks;
    const hourOffset = Math.floor(tProgress * durationHours);
    const currentHour = (startHour + hourOffset) % 24;
    const timeOfDay = mapHourToTimeOfDay(currentHour);
    const targetEnergy = (curve ? (curve[timeOfDay] ?? ENERGY_CURVE_DEFAULT) : ENERGY_CURVE_DEFAULT);

    // Candidate pool: energy within ±12, not already used
    let candidates = allTracks.filter(
      (t) => !usedIds.has(t.id) && Math.abs((t.energy ?? 50) - targetEnergy) <= 12
    );

    // Fallback: if no candidates in range, widen to ±25
    if (candidates.length === 0) {
      candidates = allTracks.filter(
        (t) => !usedIds.has(t.id) && Math.abs((t.energy ?? 50) - targetEnergy) <= 25
      );
    }

    // Last resort: any unused track
    if (candidates.length === 0) {
      candidates = allTracks.filter((t) => !usedIds.has(t.id));
    }

    if (candidates.length === 0) break; // all tracks used

    // Pick best transition from previous track
    const prev = result[result.length - 1];
    let next;
    if (prev) {
      // Sort by transition score descending, pick best
      const scored = candidates.map((t) => ({ t, score: transitionScore(prev, t) }));
      scored.sort((a, b) => b.score - a.score);
      next = scored[0].t;
    } else {
      // First track: pick closest to energy target
      candidates.sort((a, b) =>
        Math.abs((a.energy ?? 50) - targetEnergy) - Math.abs((b.energy ?? 50) - targetEnergy)
      );
      next = candidates[0];
    }

    result.push(next);
    usedIds.add(next.id);
  }

  return result;
}

export {
  generatePlaylist,
  transitionScore,
  getCurrentTimeProfile,
  getSmartNextTrack,
  getSmartNextTrackV1,
  historyDecayBoost,
  mmrRerank,
  getContextScore,
  ENERGY_CURVES,
  BUSINESS_PROFILES,
  TIME_PROFILES,
};
